import React from 'react';
import { useTranslation } from 'react-i18next';
import CropSurface from './CropSurface';
import BannerIconGlyph from './BannerIconGlyph';
import {
  FULL_PREVIEW_SLOTS,
  getPreviewSlots,
  getTemplateFieldLimit,
  type PreviewSlots,
  type buildTemplatePreviewModel,
} from '../model/bannerModel';

type Tag = { text: string; bg: string; color: string } | null;
type Model = ReturnType<typeof buildTemplatePreviewModel>;
type Crop = {
  zoom?: number | null;
  offsetX?: number | null;
  offsetY?: number | null;
  zoomLibre?: boolean;
};

interface PreviewProps {
  model: Model;
  crop?: Crop;
  frameRatio?: number;
  imageShade?: unknown;
  hoverOverlay?: boolean;
  slots?: PreviewSlots;
}

const EMPTY = '\u00A0';

function slotClass(className: string, empty: boolean) {
  return [className, empty ? 'is-empty-slot' : ''].filter(Boolean).join(' ');
}

function TagBadge({
  tag,
  className,
  allowed = true,
}: {
  tag: Tag;
  className: string;
  allowed?: boolean;
}) {
  if (!allowed) return null;
  if (!tag) {
    return (
      <div aria-hidden='true' className={slotClass(className, true)}>
        {EMPTY}
      </div>
    );
  }
  return (
    <div className={className} style={{ background: tag.bg, color: tag.color }}>
      {tag.text}
    </div>
  );
}

function IconsRow({
  icons,
  className,
  allowed = true,
}: {
  icons: Model['icons'];
  className: string;
  allowed?: boolean;
}) {
  const { t } = useTranslation(['banners']);
  if (!allowed) return null;
  if (!icons.items.length) {
    return (
      <div aria-hidden='true' className={slotClass(className, true)}>
        <div className='preview-icon-item'>
          <span className='preview-icon-wrap'>
            <span className='banner-icon-glyph' />
          </span>
          <span className='preview-icon-label'>{EMPTY}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={className} style={{ color: icons.color }}>
      {icons.items.map((icon) => (
        <div className='preview-icon-item' key={icon.id}>
          <span className='preview-icon-wrap'>
            <BannerIconGlyph src={icon.src} title={t(icon.labelKey)} />
          </span>
          <span className='preview-icon-label'>{t(icon.labelKey)}</span>
        </div>
      ))}
    </div>
  );
}

function IconsInline({ icons, allowed = true }: { icons: Model['icons']; allowed?: boolean }) {
  const { t } = useTranslation(['banners']);
  if (!allowed) return null;
  if (!icons.items.length) {
    return (
      <div aria-hidden='true' className={slotClass('row-icons-2', true)}>
        {EMPTY}
      </div>
    );
  }
  return (
    <div className='row-icons-2' style={{ color: icons.color }}>
      {icons.items.map((icon) => (
        <BannerIconGlyph key={icon.id} src={icon.src} title={t(icon.labelKey)} />
      ))}
    </div>
  );
}

function PriceBlock({
  className,
  labelClassName,
  descuento,
  precio,
  extra,
  allowed = true,
}: {
  className: string;
  labelClassName: string;
  descuento?: string;
  precio?: string;
  extra?: string;
  allowed?: boolean;
}) {
  if (!allowed) return null;
  const empty = !descuento && !precio && !extra;
  return (
    <div aria-hidden={empty || undefined} className={slotClass(className, empty)}>
      <div className={slotClass(labelClassName, !descuento)}>{descuento || EMPTY}</div>
      <div className={slotClass('value', !precio)}>{precio || EMPTY}</div>
      <div className={slotClass('detail', !extra)}>{extra || EMPTY}</div>
    </div>
  );
}

function TitleSlot({
  className,
  title,
  allowed = true,
  reserveChars,
}: {
  className: string;
  title?: string;
  allowed?: boolean;
  reserveChars?: number | null;
}) {
  if (!allowed) return null;
  const text = title || EMPTY;
  if (!reserveChars) {
    return (
      <div aria-hidden={!title || undefined} className={slotClass(className, !title)}>
        {text}
      </div>
    );
  }
  return (
    <div aria-hidden={!title || undefined} className={slotClass(className, !title)}>
      <span aria-hidden='true' className='title-slot-sizer'>
        {'0'.repeat(reserveChars)}
      </span>
      <span className='title-slot-text'>{text}</span>
    </div>
  );
}

function PreviewT1({
  model,
  crop,
  frameRatio,
  imageShade,
  slots = FULL_PREVIEW_SLOTS,
}: PreviewProps) {
  const bullets = [model.bullets[0] || '', model.bullets[1] || ''];
  const showContent = slots.bullets || slots.precio;
  return (
    <div className='preview-canvas preview-1'>
      <CropSurface
        className='hero-1'
        crop={crop}
        frameRatio={frameRatio}
        imageShade={imageShade}
        imageUrl={model.imageUrl}
        mobileImageUrl={model.mobileImageUrl}
      >
        <TagBadge allowed={slots.tags} className='top-tag' tag={model.tagBadge} />
        <TagBadge allowed={slots.tags} className='tag' tag={model.tagCombined} />
      </CropSurface>
      <TitleSlot
        allowed={slots.titulo}
        className='title-1'
        reserveChars={getTemplateFieldLimit(1, 'titulo')}
        title={model.title}
      />
      <IconsRow allowed={slots.icons} className='row-icons-1' icons={model.icons} />
      {showContent ? (
        <div className='content-1'>
          {slots.bullets ? (
            <div className='bullets-1'>
              {bullets.map((bullet, index) => (
                <div
                  aria-hidden={!bullet || undefined}
                  className={slotClass('', !bullet) || undefined}
                  key={index}
                >
                  {bullet ? `• ${bullet}` : EMPTY}
                </div>
              ))}
            </div>
          ) : null}
          <PriceBlock
            allowed={slots.precio}
            className='price-side-1'
            descuento={model.descuento}
            extra={model.extra}
            labelClassName='preview-price-label'
            precio={model.precio}
          />
        </div>
      ) : null}
    </div>
  );
}

function PreviewT2({
  model,
  crop,
  hoverOverlay,
  frameRatio,
  imageShade,
  slots = FULL_PREVIEW_SLOTS,
}: PreviewProps) {
  const { t } = useTranslation(['banners']);
  return (
    <CropSurface
      className={['preview-canvas', 'preview-2', hoverOverlay ? 'preview-2--hover' : '']
        .filter(Boolean)
        .join(' ')}
      crop={crop}
      frameRatio={frameRatio}
      imageShade={imageShade}
      imageUrl={model.imageUrl}
      mobileImageUrl={model.mobileImageUrl}
    >
      <TagBadge allowed={slots.tags} className='top-tag cat-2' tag={model.tagBadge} />
      <TitleSlot allowed={slots.titulo} className='title-2' title={model.title} />
      <TagBadge allowed={slots.tags} className='tag-2' tag={model.tagCombined} />
      <IconsInline allowed={slots.icons} icons={model.icons} />
      <PriceBlock
        allowed={slots.precio}
        className='price-side-2'
        descuento={model.descuento}
        extra={model.extra}
        labelClassName='preview-price-label'
        precio={model.precio}
      />
      {hoverOverlay ? (
        <div className='preview-hover'>
          {model.description ? (
            <div className='preview-hover__description'>{model.description}</div>
          ) : null}
          <span className='preview-hover__info'>{t('banners:v1.hoverInfo')}</span>
        </div>
      ) : null}
    </CropSurface>
  );
}

function PreviewT3({
  model,
  crop,
  frameRatio,
  imageShade,
  slots = FULL_PREVIEW_SLOTS,
}: PreviewProps) {
  return (
    <CropSurface
      className='preview-canvas preview-3'
      crop={crop}
      frameRatio={frameRatio}
      imageShade={imageShade}
      imageUrl={model.imageUrl}
      mobileImageUrl={model.mobileImageUrl}
    >
      <TagBadge allowed={slots.tags} className='cat-3' tag={model.tagBadge} />
      <div className='body-3'>
        <div className='text-3'>
          <TitleSlot allowed={slots.titulo} className='headline-3' title={model.title} />
          <TagBadge allowed={slots.tags} className='tag-3' tag={model.tagCombined} />
        </div>
        {slots.icons || slots.precio ? (
          <div className='lower-3'>
            <IconsRow allowed={slots.icons} className='row-icons-3' icons={model.icons} />
            <PriceBlock
              allowed={slots.precio}
              className='price-card-3'
              descuento={model.descuento}
              extra={model.extra}
              labelClassName='preview-price-label'
              precio={model.precio}
            />
          </div>
        ) : null}
      </div>
    </CropSurface>
  );
}

function PreviewFullBleed({
  model,
  crop,
  frameRatio,
  imageShade,
  previewClass,
}: PreviewProps & { previewClass: string }) {
  return (
    <CropSurface
      className={['preview-canvas', previewClass, model.imageUrl ? '' : 'is-empty']
        .filter(Boolean)
        .join(' ')}
      crop={crop}
      frameRatio={frameRatio}
      imageShade={imageShade}
      imageUrl={model.imageUrl}
      mobileImageUrl={model.mobileImageUrl}
      role='img'
    />
  );
}

function PreviewT4(props: PreviewProps) {
  return <PreviewFullBleed {...props} previewClass='preview-4' />;
}

function PreviewT5(props: PreviewProps) {
  return <PreviewFullBleed {...props} previewClass='preview-5' />;
}

function PreviewT6({
  model,
  crop,
  frameRatio,
  imageShade,
  slots = FULL_PREVIEW_SLOTS,
}: PreviewProps) {
  return (
    <CropSurface
      className='preview-canvas preview-6'
      crop={crop}
      frameRatio={frameRatio}
      imageShade={imageShade}
      imageUrl={model.imageUrl}
      mobileImageUrl={model.mobileImageUrl}
    >
      <TagBadge allowed={slots.tags} className='cat-6' tag={model.tagBadge} />
      <div className='body-6'>
        <div className='text-6'>
          <TitleSlot allowed={slots.titulo} className='headline-6' title={model.title} />
          <TagBadge allowed={slots.tags} className='tag-6' tag={model.tagCombined} />
        </div>
        {slots.icons || slots.precio ? (
          <div className='lower-6'>
            <IconsRow allowed={slots.icons} className='row-icons-6' icons={model.icons} />
            <PriceBlock
              allowed={slots.precio}
              className='price-card-6'
              descuento={model.descuento}
              extra={model.extra}
              labelClassName='preview-price-label'
              precio={model.precio}
            />
          </div>
        ) : null}
      </div>
    </CropSurface>
  );
}

const PREVIEWS: Record<1 | 2 | 3 | 4 | 5 | 6, React.ComponentType<PreviewProps>> = {
  1: PreviewT1,
  2: PreviewT2,
  3: PreviewT3,
  4: PreviewT4,
  5: PreviewT5,
  6: PreviewT6,
};

type TemplateCardProps = {
  template: number;
  model: Model;
  crop?: Crop;
  frameRatio?: number;
  imageShade?: unknown;
  hoverOverlay?: boolean;
  slots?: PreviewSlots;
};

export default function TemplateCard({
  template,
  model,
  crop,
  frameRatio,
  imageShade,
  hoverOverlay,
  slots,
}: TemplateCardProps) {
  const Preview = PREVIEWS[template as keyof typeof PREVIEWS] || PreviewT1;
  const resolvedSlots = slots || getPreviewSlots({ hoverOverlay });
  return (
    <Preview
      crop={crop}
      frameRatio={frameRatio}
      hoverOverlay={hoverOverlay}
      imageShade={imageShade}
      model={model}
      slots={resolvedSlots}
    />
  );
}
